/**
 * KASA KODU QR (kullanıcı kararı 18.10.2026, seçenek 1): kodu QR olarak
 * GÖSTERME ve telefon kamerasıyla OKUTMA.
 *
 * İki açık kaynak parça, tam sürümle sabit ve gözden geçirilmiş (ağ,
 * depolama, dinamik kod yok): `qrcode-generator` 2.0.4 (çizer), `jsqr` 1.4.0
 * (okur). İkisi de yalnızca gerektiğinde yüklenir (ana paket büyümesin).
 *
 * QR'ın İÇİNDE yalnızca kanonik kod (26 karakter, web adresi değil): başka
 * bir uygulama okursa yalnızca bir metin görür. Kamera görüntüsü cihazda
 * işlenir, hiçbir yere gönderilmez; kod okununca kamera hemen kapanır.
 */

import { M } from "../dil";

/** QR modülleri: `true` = koyu. Kenar boşluğu YOK (çizen ekler). */
export async function qrMatrisi(
  metin: string,
  kip: "Alphanumeric" | "Byte" = "Alphanumeric",
): Promise<readonly (readonly boolean[])[]> {
  const { default: qrcode } = await import("qrcode-generator");
  // Kod alfabesi (Crockford base32, büyük harf) QR'ın alfasayısal kipine
  // sığar: daha küçük, daha kolay okunan QR. Link (sanal hesap paylaşımı)
  // bayt kipinde ve en düşük düzeltmeyle (L) — uzun içerik sığsın.
  const q = qrcode(0, kip === "Byte" ? "L" : "M");
  q.addData(metin, kip);
  try {
    q.make();
  } catch {
    throw new Error(M.yedek.qr.sigmiyor);
  }
  const n = q.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => q.isDark(r, c)));
}

/** Bir kare (RGBA) içindeki QR'ın metni; yoksa null. */
export async function qrCoz(veri: Uint8ClampedArray, genislik: number, yukseklik: number): Promise<string | null> {
  const { default: jsQR } = await import("jsqr");
  const r = jsQR(veri, genislik, yukseklik, { inversionAttempts: "attemptBoth" });
  return r === null ? null : r.data;
}

export interface Kare {
  readonly veri: Uint8ClampedArray;
  readonly genislik: number;
  readonly yukseklik: number;
}

export interface Kamera {
  /** Kameranın canlı görüntüsü (ekranda gösterilir); testte null olabilir. */
  readonly akis: MediaStream | null;
  /** O anki kare; görüntü henüz gelmediyse null. */
  kare(): Kare | null;
  kapat(): void;
}

export class KameraHatasi extends Error {}

/** Arka kamerayı açar. Hatalar kullanıcı cümlesiyle `KameraHatasi`. */
export async function varsayilanKameraAc(): Promise<Kamera> {
  const md = globalThis.navigator?.mediaDevices;
  if (md?.getUserMedia === undefined) {
    throw new KameraHatasi(M.yedek.qr.kameraYok);
  }
  let akis: MediaStream;
  try {
    akis = await md.getUserMedia({ video: { facingMode: "environment" }, audio: false });
  } catch (e: unknown) {
    const ad = (e as { name?: string } | null)?.name;
    if (ad === "NotAllowedError" || ad === "SecurityError") {
      throw new KameraHatasi(M.yedek.qr.kameraIzni);
    }
    if (ad === "NotFoundError" || ad === "OverconstrainedError") {
      throw new KameraHatasi(M.yedek.qr.kameraBulunamadi);
    }
    throw new KameraHatasi(M.yedek.qr.kameraAcilamadi);
  }
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = akis;
  await video.play().catch(() => undefined);
  const tuval = document.createElement("canvas");
  const baglam = tuval.getContext("2d", { willReadFrequently: true });
  return {
    akis,
    kare() {
      const g = video.videoWidth;
      const y = video.videoHeight;
      if (baglam === null || g === 0 || y === 0) return null;
      tuval.width = g;
      tuval.height = y;
      baglam.drawImage(video, 0, 0, g, y);
      return { veri: baglam.getImageData(0, 0, g, y).data, genislik: g, yukseklik: y };
    },
    kapat() {
      for (const iz of akis.getTracks()) iz.stop();
      video.srcObject = null;
    },
  };
}

/** Testler kamerayı ve çözücüyü buradan değiştirir; üretimde gerçekleri. */
export const kameraAyari: {
  ac: () => Promise<Kamera>;
  coz: (veri: Uint8ClampedArray, genislik: number, yukseklik: number) => Promise<string | null>;
  araMs: number;
} = { ac: varsayilanKameraAc, coz: qrCoz, araMs: 250 };
